import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import produce from 'immer'
import { Domains } from '@ally/data-types-host'
import { withAppHeaders, withContentType, toJSON } from '@ally/fido'
import { flow } from '@ally/utilitarian'
import { mergeDeepRight, pipe } from 'ramda'
import { CustomizationOptions, Widget } from '@ally/data-types-customization'
import { LoadState } from '@ally/use-data-domain'
import { useAccountTypes } from '../../providers'
import { useHostServices } from '../../HostServices'
import { CustomizeDataObj, DashboardView, WidgetKey } from '../types'
import {
  getEligibleWidgets,
  getFormattedData,
  getUpdatedCustomizeDataObj,
  reorderWidgets,
} from '../utils'
import customizeDashboardImmerReducer, {
  CustomizeState,
  CustomizeDispatch,
} from './CustomizeDashboardReducer'
import { saveCustomizationsUtil, saveDashboardView } from './utils'
import { WidgetsConfig } from '../Widgets'
import { useBreakdownEligibility } from '../../hooks'
import { getRoles } from '../../utils'

type WidgetFunc = (widget: Widget) => Widget

export const defaultCustomizeApiAlert = {
  shouldRender: false,
  type: '',
  isDashboardError: false,
}

function getDashboardView(
  resp: CustomizationOptions | undefined,
): DashboardView {
  const customizedView = resp?.views.find(({ name }) => name === 'DASHBOARD')
    ?.value
  return customizedView
    ? (customizedView as DashboardView)
    : // this scenario seems impossible so ignoring coverage
      /* istanbul ignore next */
      DashboardView.DEFAULT
}

export type SaveDataFunc = (fetchProps: {
  payload: Partial<CustomizeDataObj>
}) => Promise<{ payload: CustomizationOptions | null }>

type AdditionalFunctions = {
  saveCustomizations: () => void
  setDashboardView: (view: DashboardView) => void
}

export type CustomizeDashboardStateAndFunctions = CustomizeState &
  AdditionalFunctions

export type CustomizeDashboardContextValue = [
  CustomizeDashboardStateAndFunctions,
  CustomizeDispatch,
]

export const CustomizeDashboardContext = createContext<
  CustomizeDashboardContextValue | undefined
>(undefined)

const NEVER_SAVED_CUSTOMIZATIONS = 'NEVER_SAVED_CUSTOMIZATIONS'

export const CustomizeDashboardProvider: React.FC = ({ children }) => {
  const { hostData, utils, featureFlags, session } = useHostServices()
  const {
    activeDepositAccounts,
    hasAtLeastOneActiveAccount,
    hasOpenSdaAccounts,
    investAccounts,
    isBankOutage,
    isInvestOutage,
    isMortgageUser,
    isWealthFullOutage,
    isWealthProspect,
    openDepositAccounts,
    stkSummary,
    isDebtPlanUser,
    isCCUser,
    allCCAccounts,
    allAutoAccounts,
  } = useAccountTypes()

  const firstRender = useRef(true)

  const roles = getRoles(session)
  const isAutoOnlyUser = roles.isOnly('auto')

  const defaultCustomizationData = getFormattedData({
    activeDepositAccounts: activeDepositAccounts ?? [],
    investAccounts,
    isBankOutage,
    isInvestOutage,
    isWealthFullOutage,
    isWealthProspect,
    isMortgageCustomer: isMortgageUser,
    isAutoCustomer:
      featureFlags.variation('FF_SPOG_WEB_UI', false) && roles.auto,
    isDebtPlanUser,
    isCCUser,
    creditCardAccounts: allCCAccounts,
    autoAccounts: allAutoAccounts,
  })

  const hostDataCustomization = hostData[Domains.CUSTOMIZATION]

  const customizeResp = hostDataCustomization.data?.find(
    ({ channel }) => channel === 'WEB',
  )

  const customizeRespError = hostDataCustomization.status.loadState === 'failed'

  const isCustomizationEnabled =
    featureFlags.variation('FF_snapshot-customization', true) &&
    !customizeRespError &&
    !isAutoOnlyUser

  const hasBreakdownWidgetAdded = !!customizeResp?.widgets.find(custWidget => {
    return custWidget.name === 'accountsOverviewWidget' && custWidget.enabled
  })

  const showBreakdown = useBreakdownEligibility(hasBreakdownWidgetAdded)

  const showWeeklyFinancialInsights =
    hasAtLeastOneActiveAccount &&
    featureFlags.variation('FF_invest-widget', true)

  const showBadgeWidget = featureFlags.variation('FF_badge-widget', true)

  /**
   * Eligible widgets are all widgets available to the user
   * that meet eligibility requirements.
   * i.e. for the stk widget, user has a bucket or booster and
   * the widget FF is enabled. This property is used to determine
   * which widgets a user can see and overrides any previous
   * customization settings which may no longer be valid.
   */
  const eligibleWidgets = useMemo(() => {
    // Eligible if account is open, not IRA, and CD is unfunded
    function hasTransferAccounts(): boolean {
      const filterFundedCds = openDepositAccounts.filter(account => {
        if (account.type !== 'CDA') {
          return true
        }
        const isCD = account.type === 'CDA'
        return isCD && account.balance?.current === 0
      })
      return !filterFundedCds.every(account => !!account.indicator?.retirement)
    }
    return getEligibleWidgets(
      WidgetsConfig({
        stkSummary,
        hasTransferAccounts,
        hasOpenSdaAccounts,
        showBreakdown,
        showWeeklyFinancialInsights,
        showBadgeWidget,
      }),
    )
  }, [
    stkSummary,
    hasOpenSdaAccounts,
    showBreakdown,
    showWeeklyFinancialInsights,
    openDepositAccounts,
    showBadgeWidget,
  ])

  const customizeApiData = customizeResp?.customizationId
    ? getUpdatedCustomizeDataObj({
        defaultCustomizations: defaultCustomizationData,
        customizationResponse: customizeResp,
        eligibleWidgets,
        isDebtPlanUser,
      })
    : defaultCustomizationData

  const widgetsFromApi =
    // this exists because the old API didn't allow for an empty array to be saved, we can probably only remove this if the database is purged of this fake widget
    customizeResp?.widgets[0]?.name === 'none' ? [] : customizeApiData.widgets

  // The database may still contain widgets that don't have an enabled property since this was added after an API update, widgets without an enabled prop should be assumed `enabled: true`
  const isLegacyWidgetsCustomer = !!customizeResp?.widgets.some(
    widget => widget.enabled === undefined,
  )

  /** Default widgets on function */
  function getWidgets(): Widget[] {
    const additionalEligibleWidgets: Widget[] = Object.keys(eligibleWidgets)
      // filter out currently added widgets
      .filter(widgetName => !widgetsFromApi.find(w => w.name === widgetName))
      // add enabled prop
      .map(widgetName => ({
        name: widgetName,
        // order gets modified below in `reorderWidgets`
        order: 0,
        enabled: true,
      }))

    const forceEnabled: WidgetFunc = w => ({ ...w, enabled: true })

    const disableIneligibleWidgets: WidgetFunc = widget => {
      if (
        eligibleWidgets[widget.name as WidgetKey] === undefined &&
        widget.enabled
      ) {
        return { ...widget, enabled: false }
      }
      return widget
    }

    if (isLegacyWidgetsCustomer) {
      return reorderWidgets(
        widgetsFromApi
          .map(pipe(forceEnabled, disableIneligibleWidgets))
          .concat(additionalEligibleWidgets),
      )
    }

    return reorderWidgets(
      widgetsFromApi
        .map(disableIneligibleWidgets)
        .concat(additionalEligibleWidgets),
    )
  }

  const widgets = getWidgets()

  //  if it is the user's first time ever saving customizations AND they use the view toggle, we need to assign the widgets since the old behavior would send an empty widget array
  if (firstRender.current && !customizeResp?.customizationId) {
    firstRender.current = false
    customizeApiData.widgets = widgets
  }

  function determineDashboardView(): DashboardView {
    // if widgets are available we can check the customization resp for the user's customized view
    return isCustomizationEnabled &&
      Object.keys(eligibleWidgets).length > 0 &&
      widgets.some(w => w.enabled)
      ? getDashboardView(customizeResp)
      : DashboardView.DEFAULT
  }

  const initialState: CustomizeState = {
    // updates when saveCustomizations is successful
    customizeApiData,
    customizeApiAlert: defaultCustomizeApiAlert,
    dashboardView: determineDashboardView(),
    isCustomizeModalOpen: false,
    isCustomizationEnabled,
    // updates when saveCustomizations is successful
    widgets, // current widgets added
    eligibleWidgets, // all possible widgets to add
    customizeDataToPatch: {},
    hasCustomizeChangesToSave: false,
    isSaving: false,
  }

  const [state, customizeDispatch] = useReducer(
    // immer
    produce(customizeDashboardImmerReducer),
    initialState,
  )

  // Ensures the customized snapshot is updated when the authenticated FF's are returned, useful for pilot segments
  useEffect(() => {
    const authFlagsLoaded = featureFlags.loadState === LoadState.LOADED
    if (authFlagsLoaded) {
      customizeDispatch({
        type: 'set customizeApiData',
        payload: customizeApiData,
      })

      customizeDispatch({
        type: 'set widgets',
        payload: widgets,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.loadState])

  // rare case
  const aWidgetHasNoEnabledProp =
    Object.keys(eligibleWidgets).length > 0 &&
    (state.widgets.length === 0 ||
      state.widgets.some(w => w.enabled === undefined))

  useEffect(() => {
    // if somehow the state.widgets does not have the enabled prop this will make sure that it adds it back
    if (aWidgetHasNoEnabledProp) {
      customizeDispatch({
        type: 'set widgets',
        payload: widgets,
      })
    }
    // `widgets` is recalculated on every render but this code is protected by an if statement, so it is okay to run a useEffect that will do nothing
  }, [aWidgetHasNoEnabledProp, widgets])

  useEffect(() => {
    if (customizeRespError) {
      customizeDispatch({
        type: 'CustomizeDashboard API alert',
        payload: {
          shouldRender: true,
          type: 'error',
          isDashboardError: true,
        },
      })
    }
  }, [customizeRespError])

  // The LD call always fires twice. So the 1st call will return the default values for FF's.
  // Then the 2nd network call will include the current user and return the appropriate values for that user.
  // Ex: If the 1st call is true for customization and the 2nd one is false we need to update that value in our reducer state
  useEffect(() => {
    // this if statement skips the first render useEffect call
    if (state.isCustomizationEnabled !== isCustomizationEnabled) {
      customizeDispatch({
        type: 'set isCustomizationEnabled',
        payload: isCustomizationEnabled,
      })
    }
  }, [isCustomizationEnabled, state.isCustomizationEnabled])

  const customizationId =
    (state.customizeApiData as CustomizationOptions)?.customizationId ??
    // user has never saved customizations
    NEVER_SAVED_CUSTOMIZATIONS

  const guid = session.data?.guid

  const contextValue: CustomizeDashboardContextValue = useMemo(() => {
    const newUser = customizationId === NEVER_SAVED_CUSTOMIZATIONS
    const method = newUser ? 'POST' : 'PATCH'

    const customizeDataCall: SaveDataFunc = ({ payload }) => {
      const afgVersionNumber = featureFlags.variation(
        'FF_afg-customization-version',
        1,
      )
      const afgGreaterThanZero = afgVersionNumber > 0
      const afgVersionUrl = afgGreaterThanZero ? `/v${afgVersionNumber}` : ''
      const endpointUrl = newUser
        ? `/acs${afgVersionUrl}/customers/${guid}/customizations`
        : `/acs${afgVersionUrl}/customers/${guid}/customizations/${customizationId}`
      const CSRFChallengeToken = afgGreaterThanZero
        ? null
        : session.data?.CSRFChallengeToken
      const bearerToken = afgGreaterThanZero
        ? session.data?.access_token
        : session.data?.AwsAccessToken ||
          (sessionStorage &&
            JSON.parse(sessionStorage.getItem('AwsAccessToken') || 'null'))

      return fetch(
        endpointUrl,
        flow(
          withAppHeaders,
          withContentType,
        )({
          method,
          headers: {
            ...(CSRFChallengeToken ? { CSRFChallengeToken } : {}),
            Authorization: `Bearer ${bearerToken}`,
          },
          body: JSON.stringify(payload),
        }),
      )
        .then(response => {
          if (!response.ok) {
            throw new Error(
              `Customization ${method} failed with status of ${response.status}`,
            )
          }

          return method === 'POST'
            ? toJSON<CustomizationOptions>(response)
            : null
        })
        .then(maybeCustomizationOptions => {
          return maybeCustomizationOptions
            ? { payload: maybeCustomizationOptions }
            : { payload: null }
        })
    }

    const payload =
      method === 'POST'
        ? mergeDeepRight(state.customizeApiData, state.customizeDataToPatch)
        : state.customizeDataToPatch

    async function saveCustomizations(): Promise<void> {
      await saveCustomizationsUtil(
        customizeDispatch,
        payload,
        customizeDataCall,
      )

      // update customizations for the app
      // Ex: customization is also used in the <AccountsDropdown> of the host app
      hostDataCustomization.update()
    }
    const existingViews = state.customizeApiData.views
    function setDashboardView(view: DashboardView): void {
      saveDashboardView(
        customizeDispatch,
        view,
        customizeDataCall,
        existingViews,
        method,
        payload,
        hostDataCustomization,
      )
    }
    return [
      {
        ...state,
        eligibleWidgets,
        setDashboardView,
        saveCustomizations,
      },
      customizeDispatch,
    ]
  }, [
    customizationId,
    eligibleWidgets,
    guid,
    hostDataCustomization,
    session.data?.access_token,
    state,
    utils.fetch,
  ])

  return (
    <CustomizeDashboardContext.Provider value={contextValue}>
      {children}
    </CustomizeDashboardContext.Provider>
  )
}

export function useCustomizeDashboard(): CustomizeDashboardContextValue {
  const context = useContext(CustomizeDashboardContext)
  if (context === undefined)
    throw new Error(
      'useCustomizeDashboard must be used within a CustomizeDashboardProvider',
    )
  return context
}
